import { useEffect } from "react";

/**
 * Memory-safety guard for long-lived SPA tabs.
 *
 * Single-page apps that stay open for hours accumulate memory regardless
 * of how disciplined our cleanup is — React fiber retention, Framer
 * Motion tween state, WebSocket buffer churn, V8 heap fragmentation, and
 * the renderer's own DOM/style caches all grow with time. Chrome
 * eventually SBOX_FATAL_MEMORY_EXCEEDED's the renderer ("Aw, Snap!
 * Error code: 5").
 *
 * Three layered defences:
 *
 *   1. **Hidden-tab reload** — on `visibilitychange` back to visible,
 *      if the tab has been hidden for ≥30 minutes, reload. Catches the
 *      "left it open overnight" case.
 *
 *   2. **Active-session ceiling** — if the tab has been alive for ≥6
 *      hours wall-clock, reload at the next quiet moment regardless of
 *      heap state. Belt-and-suspenders.
 *
 *   3. **Last-resort heap floor** — if `performance.memory` is exposed
 *      AND the heap goes truly catastrophic (≥ 95% of jsHeapSizeLimit
 *      AND > 1.5 GB used in absolute terms), force a reload. Both
 *      conditions are required because `jsHeapSizeLimit` is unreliable
 *      across Chromium configs — some machines report values that make
 *      a normal SPA look "full" right after load. We only trigger when
 *      both signals agree the heap is genuinely huge.
 *
 * Why no aggressive heap watchdog: an earlier version polled every 15 s
 * and force-reloaded at 70% / 85% ratios. On some Chromium configs
 * those ratios are hit immediately on load, producing a reload-every-8s
 * flicker loop. The simple visibility + session-ceiling reload covers
 * the actual OOM window without that risk.
 */

const HIDDEN_RELOAD_MS = 30 * 60 * 1000; // 30 minutes hidden → reload on return
const SESSION_CEILING_MS = 6 * 60 * 60 * 1000; // 6 hours alive → reload at next quiet
const HEAP_CHECK_INTERVAL_MS = 60_000;
const HEAP_RATIO_FLOOR = 0.95; // both gates must agree before we reload
const HEAP_ABS_FLOOR_BYTES = 1.5 * 1024 * 1024 * 1024; // 1.5 GB
const QUIET_INPUT_MS = 5_000;
const SCHEDULE_DEADLINE_MS = 30_000;

interface ChromeMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

function getMemory(): ChromeMemory | null {
  const perf = typeof performance !== "undefined"
    ? (performance as Performance & { memory?: ChromeMemory })
    : null;
  return perf?.memory ?? null;
}

export function useStaleTabReload(): void {
  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return;

    const sessionStart = Date.now();
    let hiddenAt: number | null = null;
    let lastInputAt = Date.now();
    let reloaded = false;

    const isQuiet = () =>
      document.visibilityState === "hidden" ||
      Date.now() - lastInputAt > QUIET_INPUT_MS;

    const reloadWhenQuiet = (reason: string) => {
      if (reloaded) return;
      reloaded = true;
      // eslint-disable-next-line no-console
      console.info(`[stale-tab-reload] reloading at next quiet (${reason})`);
      const deadline = Date.now() + SCHEDULE_DEADLINE_MS;
      const tick = () => {
        if (isQuiet() || Date.now() > deadline) {
          window.location.reload();
          return;
        }
        setTimeout(tick, 1_000);
      };
      tick();
    };

    // ── 1. Hidden-tab reload ──
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt === null) return;
      const hiddenFor = Date.now() - hiddenAt;
      hiddenAt = null;
      if (hiddenFor >= HIDDEN_RELOAD_MS) {
        reloadWhenQuiet(`hidden ${Math.round(hiddenFor / 60000)}m`);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── 2. Active-session ceiling ──
    const ceilingTimeout = setTimeout(() => {
      reloadWhenQuiet(
        `session ${Math.round((Date.now() - sessionStart) / 3600000)}h`
      );
    }, SESSION_CEILING_MS);

    // ── 3. Last-resort heap floor (both ratio AND absolute size required) ──
    let heapInterval: ReturnType<typeof setInterval> | null = null;
    if (getMemory()) {
      heapInterval = setInterval(() => {
        if (reloaded) return;
        const m = getMemory();
        if (!m || !m.jsHeapSizeLimit) return;
        const ratio = m.usedJSHeapSize / m.jsHeapSizeLimit;
        if (
          ratio >= HEAP_RATIO_FLOOR &&
          m.usedJSHeapSize >= HEAP_ABS_FLOOR_BYTES
        ) {
          reloadWhenQuiet(
            `heap floor ${Math.round(ratio * 100)}% / ${(m.usedJSHeapSize / 1e9).toFixed(2)}GB`
          );
        }
      }, HEAP_CHECK_INTERVAL_MS);
    }

    // Track input for the "quiet" decision.
    const markInput = () => {
      lastInputAt = Date.now();
    };
    window.addEventListener("keydown", markInput, { passive: true });
    window.addEventListener("pointerdown", markInput, { passive: true });
    window.addEventListener("scroll", markInput, { passive: true, capture: true });

    return () => {
      if (heapInterval) clearInterval(heapInterval);
      clearTimeout(ceilingTimeout);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("keydown", markInput);
      window.removeEventListener("pointerdown", markInput);
      window.removeEventListener("scroll", markInput, true);
    };
  }, []);
}
